// Primitivo de estado de listagem/tabela sincronizado com a URL (TanStack Router).
// Serializa page/pageSize/sort/filtros/extras no search param, omitindo defaults —
// URL limpa, linkável e restaurável no reload. Genérico: cada tela passa suas
// chaves de filtro, campos ordenáveis e page sizes. Ver webhook-logs-screen.tsx.
import { useCallback, useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

export interface TableSearchConfig {
  filterKeys: readonly string[];
  sortFields: readonly string[];
  defaultSort: string;
  defaultSortDir: "asc" | "desc";
  pageSizes: readonly number[];
  defaultPageSize: number;
  extraKeys?: readonly string[];
}

export interface NormalizedTableSearch {
  filters: Record<string, string | undefined>;
  extras: Record<string, string | undefined>;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: "asc" | "desc";
}

function asString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

// raw (search param cru) → estado normalizado, com clamp para os defaults.
export function normalizeTableSearch(
  raw: Record<string, unknown>,
  config: TableSearchConfig,
): NormalizedTableSearch {
  const filters: Record<string, string | undefined> = {};
  for (const k of config.filterKeys) filters[k] = asString(raw[k]);

  const extras: Record<string, string | undefined> = {};
  for (const k of config.extraKeys ?? []) extras[k] = asString(raw[k]);

  const pageNum = Number(raw.page);
  const page = Number.isInteger(pageNum) && pageNum >= 1 ? pageNum : 1;

  const sizeNum = Number(raw.pageSize);
  const pageSize = config.pageSizes.includes(sizeNum) ? sizeNum : config.defaultPageSize;

  const rawSort = asString(raw.sortBy);
  const sortBy = rawSort && config.sortFields.includes(rawSort) ? rawSort : config.defaultSort;

  const sortDir =
    raw.sortDir === "asc" || raw.sortDir === "desc" ? raw.sortDir : config.defaultSortDir;

  return { filters, extras, page, pageSize, sortBy, sortDir };
}

// Funde um patch de várias chaves de filtro num objeto de filtros existente,
// numa única passada. Extraída de setFilters (abaixo) para ser testável sem
// montar o hook (que depende de useSearch/useNavigate do router) — é a peça
// que precisa ficar correta para que um patch com múltiplas chaves (ex.: um
// DateRangePicker que entrega from+to juntos) não perca nenhuma delas.
export function mergeFilters(
  filters: Record<string, string | undefined>,
  patch: Record<string, string | undefined>,
): Record<string, string | undefined> {
  const next = { ...filters };
  for (const [key, value] of Object.entries(patch)) next[key] = value || undefined;
  return next;
}

// estado normalizado → objeto de search param, OMITINDO tudo que iguala o default
// ou está vazio (mantém a URL curta).
export function serializeTableSearch(
  state: NormalizedTableSearch,
  config: TableSearchConfig,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const k of config.filterKeys) {
    const v = state.filters[k];
    if (v) out[k] = v;
  }
  for (const k of config.extraKeys ?? []) {
    const v = state.extras[k];
    if (v) out[k] = v;
  }
  if (state.page !== 1) out.page = state.page;
  if (state.pageSize !== config.defaultPageSize) out.pageSize = state.pageSize;
  if (state.sortBy !== config.defaultSort) out.sortBy = state.sortBy;
  if (state.sortDir !== config.defaultSortDir) out.sortDir = state.sortDir;
  return out;
}

export interface TableSearchApi extends NormalizedTableSearch {
  offset: number;
  setFilter: (key: string, value?: string) => void;
  setFilters: (patch: Record<string, string | undefined>) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  toggleSort: (field: string) => void;
  setExtra: (key: string, value?: string) => void;
  clear: () => void;
}

// Hook: lê o search param da rota atual (strict:false → agnóstico à rota) e devolve
// o estado + mutadores que reescrevem a URL. Trocar filtro/sort/pageSize reseta page=1.
export function useTableSearch(config: TableSearchConfig): TableSearchApi {
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const state = useMemo(() => normalizeTableSearch(raw, config), [raw, config]);

  const apply = useCallback(
    (next: NormalizedTableSearch) => {
      // O hook é agnóstico à rota (strict:false) e pode ser usado em qualquer tela
      // futura, então o TS não consegue inferir estaticamente o schema de search
      // da rota atual a partir daqui. Runtime não é afetado — a URL é reescrita
      // com exatamente o objeto retornado por serializeTableSearch.
      navigate({ search: (() => serializeTableSearch(next, config)) as any });
    },
    [navigate, config],
  );

  const setFilter = useCallback(
    (key: string, value?: string) => {
      apply({ ...state, filters: { ...state.filters, [key]: value || undefined }, page: 1 });
    },
    [apply, state],
  );

  // Igual a setFilter, mas para quando mais de uma chave muda na MESMA interação
  // (ex.: um DateRangePicker que entrega from+to juntos). Duas chamadas
  // sequenciais de setFilter fechariam sobre o mesmo `state` da render atual e a
  // segunda apagaria a primeira — setFilters funde o patch inteiro num só apply.
  const setFilters = useCallback(
    (patch: Record<string, string | undefined>) => {
      apply({ ...state, filters: mergeFilters(state.filters, patch), page: 1 });
    },
    [apply, state],
  );

  const setExtra = useCallback(
    (key: string, value?: string) => {
      apply({ ...state, extras: { ...state.extras, [key]: value || undefined } });
    },
    [apply, state],
  );

  const setPage = useCallback(
    (page: number) => apply({ ...state, page: Math.max(1, page) }),
    [apply, state],
  );

  const setPageSize = useCallback(
    (size: number) => apply({ ...state, pageSize: size, page: 1 }),
    [apply, state],
  );

  const toggleSort = useCallback(
    (field: string) => {
      const sortDir = state.sortBy === field && state.sortDir === "desc" ? "asc" : "desc";
      apply({ ...state, sortBy: field, sortDir, page: 1 });
    },
    [apply, state],
  );

  const clear = useCallback(() => {
    apply({
      filters: {},
      extras: state.extras,
      page: 1,
      pageSize: state.pageSize,
      sortBy: config.defaultSort,
      sortDir: config.defaultSortDir,
    });
  }, [apply, state, config]);

  return {
    ...state,
    offset: (state.page - 1) * state.pageSize,
    setFilter,
    setFilters,
    setPage,
    setPageSize,
    toggleSort,
    setExtra,
    clear,
  };
}
